import { useEffect } from 'react';

/**
 * Like `useEffect`, but defers the body until the browser is idle (via
 * `requestIdleCallback`, falling back to a short `setTimeout` where it isn't
 * available). Use this for work that should run on mount but doesn't need to
 * compete with critical render — cosmetic fetches, prefetches, analytics
 * warmup, etc.
 *
 * The idle callback is cancelled if the component unmounts (or `deps` change)
 * before it fires, so the body never runs against a stale component.
 *
 * The body itself does not support returning a teardown — keep it to fire-
 * and-forget work. If you need teardown, write a plain `useEffect`.
 */
export function useOnceBrowserIdleEffect(fn: () => void, deps: React.DependencyList) {
    useEffect(() => {
        const supportsIdle = typeof window.requestIdleCallback === 'function';
        const idleId = supportsIdle
            ? window.requestIdleCallback(fn, { timeout: 3000 })
            : window.setTimeout(fn, 1500);
        return () => {
            if (supportsIdle) {
                window.cancelIdleCallback(idleId);
            } else {
                window.clearTimeout(idleId);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
